import React, { useContext, useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import * as HiIcons from "react-icons/hi";
import { getSidebarData } from "./SidebarData";
import SubMenu from "./SubMenu";
import { IconContext } from "react-icons/lib";
import PermissionGate, { useHasPermission, useHasModule } from "../../services/PermissionGate";
import "./style.css";
import defaultUserImg from "../../img/defaultUserImg.png";
import SafeImage from "../SafeImage";
import { brandIcon } from "../../utils/brandLogos";
import audioNotify from "../../assets/audio/soundNotify.mp3";
import { MdStorefront, MdSettings, MdLanguage } from "react-icons/md";
import { RiArrowDropDownLine } from "react-icons/ri";
import { FaRobot, FaArrowRight } from "react-icons/fa";
import { IoWarningOutline } from "react-icons/io5";
import { getStoredUser } from "../../utils/safeStorage";
import io from "socket.io-client";
import { ImPrinter } from "react-icons/im";
import ModalUserImg from "../ModalAddUserImg";
import ModalEditUser from "../../components/ModalEditUser";
import ModalLinkCardapio from "../ModalLinkCardapio";
import ModalLinkCardapioSalao from "../ModalLinkCardapioSalao";
import { RiMenuFoldFill, RiMenuUnfoldFill } from "react-icons/ri";
import { GiHamburgerMenu } from "react-icons/gi";
import { MdExitToApp } from "react-icons/md";
import roundTable from "../../img/round-table.png"
import entregadorIco from "../../img/entregador.png"
import { supportsReliableNotifications, isIOS } from "../../utils/deviceDetection";
import { SidebarContext } from "../../AppRoutes";
import { AuthContext } from "../../contexts/auth";
import {
  getUser,
  getVinculoEmpresa,
  updateStatusBotEmpresa,
  updateCallAtendenteEmpresa,
  changeStatusLoja,
  getDaysToExpireLicense,
  getEmpresaWithObjId,
  updatePreferredLanguage,
  getCurrentAccessToken,
} from "../../services/api";
import { changeLanguage } from '../../i18n';
import { toast } from "react-toastify";
import CustomDrawer from "../CustomDrawer";
import CustomTooltip from "../CustomTooltip/CustomTooltip";
import AtendimentoModal from "./AtendimentoModal";
import useFilaAtendimento from "../../hooks/useFilaAtendimento";

// ====================================================================
// 🎨 MODAL DE CONFIRMAÇÃO - STYLED COMPONENTS
// ====================================================================
const modalFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  animation: ${modalFadeIn} 0.2s ease-out;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 400px;
  z-index: 10000;
  animation: ${modalSlideIn} 0.25s ease-out;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$isClosing ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'};

  svg {
    font-size: 22px;
    color: ${props => props.$isClosing ? '#d97706' : '#059669'};
  }
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 20px 24px;
`;

const ModalMessage = styled.p`
  font-size: 15px;
  color: #475569;
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const ModalWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: ${props => props.$isClosing ? '#fef3c7' : '#d1fae5'};
  border-radius: 10px;
  margin-top: 8px;

  svg {
    flex-shrink: 0;
    font-size: 18px;
    color: ${props => props.$isClosing ? '#d97706' : '#059669'};
    margin-top: 1px;
  }

  span {
    font-size: 13px;
    color: ${props => props.$isClosing ? '#92400e' : '#065f46'};
    line-height: 1.4;
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px 20px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.$primary ? `
    background: ${props.$isClosing
      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
    color: white;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px ${props.$isClosing ? 'rgba(245, 158, 11, 0.4)' : 'rgba(16, 185, 129, 0.4)'};
    }
  ` : `
    background: #f1f5f9;
    color: #475569;

    &:hover {
      background: #e2e8f0;
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// ====================================================================
// 🎨 COMPONENTES STYLED REMOVIDOS - USANDO CSS CLASSES MODERNAS
// ====================================================================



// ====================================================================
// 🎯 COMPONENTE DE WIDGET DE AJUDA MODERNIZADO
// ====================================================================


// ====================================================================
// 🎯 CHAT DE SUPORTE EASYORDER
// ====================================================================
// ====================================================================
// 🎯 COMPONENTE PRINCIPAL LEFTMENU REFATORADO
// ====================================================================
const LeftMenu = () => {
  const { sidebar, setSidebar } = useContext(SidebarContext);
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['navigation', 'common']);

  // Gerar dados do sidebar com traduções
  const SidebarData = getSidebarData(t);

  // ====================================================================
  // RBAC MODULAR: verificar acesso por modulo (novo sistema)
  // ====================================================================
  const canViewPedidos = useHasModule('pedidos');
  const canViewWhatsapp = useHasModule('whatsapp');
  const canViewPdv = useHasModule('pdv');
  const canViewCaixa = useHasModule('caixa');
  const canViewEstoque = useHasModule('estoque');
  const canViewEntregadores = useHasModule('entregadores');
  const canViewInfoLoja = useHasModule('informacoes_loja');
  const canViewCardapio = useHasModule('cardapio');
  const canViewRelatorios = useHasModule('relatorios');
  const canViewSalao = useHasModule('salao');
  const canViewEquipe = useHasModule('equipe');
  const canViewMinhaConta = useHasModule('minha_conta');
  const canViewConfiguracoes = useHasModule('configuracoes');

  // Mapa de moduleKey -> resultado do hook para uso dinamico
  const moduleAccessMap = {
    'pedidos': canViewPedidos,
    'whatsapp': canViewWhatsapp,
    'pdv': canViewPdv,
    'caixa': canViewCaixa,
    'estoque': canViewEstoque,
    'entregadores': canViewEntregadores,
    'informacoes_loja': canViewInfoLoja,
    'cardapio': canViewCardapio,
    'relatorios': canViewRelatorios,
    'salao': canViewSalao,
    'equipe': canViewEquipe,
    'minha_conta': canViewMinhaConta,
    'configuracoes': canViewConfiguracoes,
  };

  // LEGADO: manter rbacPermissionMap para backward compat (componentes que ainda usam)
  const canViewCatalog = useHasPermission('catalogo:write');
  const canViewClientes = useHasPermission('clientes:read');
  const canViewImpressao = useHasPermission('impressao:manage');
  const rbacPermissionMap = {
    'catalogo:write': canViewCatalog,
    'caixa:view': canViewCaixa,
    'relatorios:view': canViewRelatorios,
    'estoque:read': canViewEstoque,
    'clientes:read': canViewClientes,
    'whatsapp:config': canViewWhatsapp,
    'empresa:billing': canViewMinhaConta,
    'users:manage': canViewEquipe,
    'empresa:config': canViewConfiguracoes,
    'entregadores:manage': canViewEntregadores,
    'mesas:manage': canViewSalao,
    'impressao:manage': canViewImpressao,
  };

  /**
   * Verifica se um item do menu deve ser exibido.
   * Prioriza moduleKey (novo sistema modular). Fallback para rbacPermission (legado).
   * Se ambos forem null, o item e sempre visivel (ex: Admin controlado via PermissionGate).
   */
  const canViewMenuItem = (item) => {
    // Novo sistema modular: verificar moduleKey
    if (item.moduleKey) {
      return moduleAccessMap[item.moduleKey] ?? true;
    }
    // Legado: verificar rbacPermission
    if (item.rbacPermission) {
      return rbacPermissionMap[item.rbacPermission] ?? true;
    }
    // Sem moduleKey nem rbacPermission = sempre visivel (controlado por PermissionGate)
    return true;
  };

  // ====================================================================
  // REFS E STATES
  // ====================================================================
  const socketRef = useRef(null);
  const userOptionsRef = useRef();
  const leftMenuRef = useRef();
  const menuStatusRoboRef = useRef();
  const menuStatusRoboRef_ = useRef();
  const atendimentoButtonRef = useRef(null);

  // ✅ Estado de inicialização para corrigir problemas de layout
  const [isInitialized, setIsInitialized] = useState(false);
  
  const [showEditUser, setEditUser] = useState(true);
  const [_idUserEdit, set_idUserEdit] = useState("");
  const [usernameEdit, setUsernameEdit] = useState("");
  const [emailEdit, setEmailEdit] = useState("");
  const [roleEdit, setRoleEdit] = useState("");
  // const [refresh, setRefresh] = useState(false); // Não utilizado atualmente
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  
  const INITIAL_DATA = {
    value: "",
    label: t('common:selectCompany'),
  };
  const [selectData, setselectData] = useState(INITIAL_DATA);
  const [dropstatusrobo, setDropStatusRobo] = useState(false);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState({ top: 0, left: 0 });
  const [statusLojaTemporario, setStatusLojaTemporario] = useState(false);
  const [showStoreStatusModal, setShowStoreStatusModal] = useState(false);
  const [isChangingStoreStatus, setIsChangingStoreStatus] = useState(false);

  // ====================================================================
  // DADOS DO USUÁRIO E EMPRESA
  // ====================================================================
  const userParse = getStoredUser();
  const userID = userParse?._id;
  const userName = userParse?.name;
  const userEmail = userParse?.email;
  var userImg = null;
  if (userParse?.user_img?.length > 0) {
    userImg = userParse.user_img[0];
  }

  const empresa = localStorage.getItem("empresa");
  const empresaParse = JSON.parse(empresa);
  const objIdEmpresa = empresaParse._id;
  const nomeEmpresa = empresaParse.name;
  const nomeEmpresaForUrl = nomeEmpresa
    .replace(/\s+/g, "-")
    .replace(/[A-Z]/g, (c) => c.toLowerCase());

  // ====================================================================
  // STATES PARA MODAIS E CONFIGURAÇÕES
  // ====================================================================
  const [showLinkCardapio, setShowLinkCardapio] = useState(false);
  const [showLinkCardapioSalao, setShowLinkCardapioSalao] = useState(false);
  const [showOptions, setUserOptions] = useState(true);
  const [showUserOptions, setShowUserOptions] = useState(false);
  const [showMobileStatusSidebar, setShowMobileStatusSidebar] = useState(false);

  // Refs para posicionamento dinâmico do dropdown
  const userOptionsButtonRef = useRef(null);
  const userOptionsDropdownRef = useRef(null);

  const [showModalImg, setModalImg] = useState(true);
  const [statusBot, setStatusBot] = useState(false);
  const [callAtendente, setCallAtendente] = useState(false);
  const [statusPrinter, setStatusPrinter] = useState("");
  const [daysToExpire, setDaysToExpire] = useState("");
  const [planType, setPlanType] = useState("");
  const [semCobrancaOuInvoice, setSemCobrancaOuInvoice] = useState(false);

  // ====================================================================
  // HOOK DE FILA DE ATENDIMENTO
  // ====================================================================
  const {
    atendimentosPendentes,
    isLoading: isLoadingAtendimentos,
    iniciarAtendimento,
    cancelarAtendimento,
    cancelarTodosAtendimentos
  } = useFilaAtendimento(objIdEmpresa, socketRef.current);


  // ====================================================================
  // STATES PARA AVISOS DE LICENÇA
  // ====================================================================
  const userCreatedAt = userParse?.createdAt ? new Date(userParse.createdAt) : null;
  const agora = new Date();
  const diferencaEmDias = userCreatedAt ? (agora - userCreatedAt) / (1000 * 60 * 60 * 24) : 999;
  const diasTesteRestantes = Math.max(Math.ceil(7 - diferencaEmDias), 0);
  const [carregandoCheckLicense, setCarregandoCheckLicense] = useState(true);

  const [showTrialAlert, setShowTrialAlert] = useState(true);
  const [showExpirationAlert, setShowExpirationAlert] = useState(true);
  const [isTrialAlertFading, setIsTrialAlertFading] = useState(false);
  const [isExpirationAlertFading, setIsExpirationAlertFading] = useState(false);

  // ====================================================================
  // STATES PARA "COMECE POR AQUI"
  // ====================================================================
  const [progressoConfiguracaoInicial, setProgressoConfiguracaoInicial] = useState(0);
  const [totalEtapasConfiguracaoInicial] = useState(9);
  const [mostrarComecePorAqui, setMostrarComecePorAqui] = useState(false);

  // ====================================================================
  // FUNÇÕES UTILITÁRIAS
  // ====================================================================
  const toggleSidebar = () => {
    setSidebar(!sidebar);
  };

  const handleLogout = () => {
    logout();
  };

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  // ====================================================================
  // FUNÇÕES DE CONFIGURAÇÃO
  // ====================================================================
  const handleSwitchChange = async () => {
    const newStatus = !statusBot;
    setStatusBot(newStatus);
    process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Status atualizado:", newStatus);
    try {
      const response = await updateStatusBotEmpresa(objIdEmpresa, newStatus);
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Resposta do servidor:", response);
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao atualizar status_bot:", error);
    }
  };

  const handleCallAtendenteChange = async () => {
    const newStatus = !callAtendente;
    setCallAtendente(newStatus);
    process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Call atendente atualizado:", newStatus);
    try {
      const response = await updateCallAtendenteEmpresa(objIdEmpresa, newStatus);
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Resposta do servidor:", response);
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao atualizar call_atendente:", error);
    }
  };

  // Mostrar modal de confirmação antes de mudar status da loja
  const handleOpenCloseLoja = () => {
    setShowStoreStatusModal(true);
  };

  // Confirmar mudança de status da loja
  const handleConfirmStoreStatus = async () => {
    setIsChangingStoreStatus(true);
    const newStatus = !statusLojaTemporario;
    try {
      const response = await changeStatusLoja(newStatus);
      if (response.status === 200) {
        setStatusLojaTemporario(newStatus);
        toast(
          newStatus
            ? t('common:storeStatus.storeClosedSuccess')
            : t('common:storeStatus.storeOpenedSuccess'),
          { type: "success", autoClose: 2000 }
        );
      } else {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Falha ao atualizar o status da loja:", response);
        toast(t('common:storeStatus.error'), { type: "error", autoClose: 2000 });
      }
    } catch (error) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao chamar a API:", error);
      toast(t('common:storeStatus.error'), { type: "error", autoClose: 2000 });
    } finally {
      setIsChangingStoreStatus(false);
      setShowStoreStatusModal(false);
    }
  };

  const handleLanguageChange = async (newLang) => {
    changeLanguage(newLang);

    // Atualizar empresa no localStorage
    try {
      const stored = localStorage.getItem('empresa');
      if (stored) {
        const data = JSON.parse(stored);
        data.preferredLanguage = newLang;
        localStorage.setItem('empresa', JSON.stringify(data));
      }
    } catch (e) {}

    // Persistir no banco (fire and forget)
    try {
      await updatePreferredLanguage(newLang);
    } catch (error) {
      console.error("[LANGUAGE] Erro ao salvar:", error);
    }
  };

  const handleToggleStatusRobo = () => {
    const newState = !dropstatusrobo;

    if (newState && menuStatusRoboRef.current) {
      // Calcular posição do dropdown baseado no botão
      const buttonRect = menuStatusRoboRef.current.getBoundingClientRect();
      const dropdownWidth = 220;
      const dropdownHeight = 140;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let top = buttonRect.bottom + 8;
      let left = buttonRect.left;

      // Ajustar se sair da direita
      if (left + dropdownWidth > windowWidth - 10) {
        left = buttonRect.right - dropdownWidth;
      }

      // Ajustar se sair da esquerda
      if (left < 10) {
        left = 10;
      }

      // Ajustar se sair de baixo
      if (top + dropdownHeight > windowHeight - 10) {
        top = buttonRect.top - dropdownHeight - 8;
      }

      setStatusDropdownPosition({ top, left });
    }

    setDropStatusRobo(newState);
  };

  // ====================================================================
  // FUNÇÕES DE USUÁRIO
  // ====================================================================
  const handleEdit = async (idToEdit) => {
    setEditUser(!showEditUser);
    setUserOptions(!showOptions);
    const response = await getUser(idToEdit);
    
    if (showEditUser) {
      set_idUserEdit(idToEdit);
      setUsernameEdit(response.data.user.name);
      setEmailEdit(response.data.user.email);
      setRoleEdit(response.data.user.role);

      if (response.data.user.vinculo_empresa) {
        const responseVinculo = await getVinculoEmpresa(idToEdit);
        if (responseVinculo.data.vinculo) {
          setselectData({
            value: responseVinculo.data.vinculo.id_empresa,
            label:
              responseVinculo.data.vinculo.id_empresa +
              " - " +
              responseVinculo.data.vinculo.cnpj +
              " - " +
              responseVinculo.data.vinculo.name,
          });
        }
        if (!responseVinculo.data.vinculo) {
          process.env.REACT_APP_ISDEVELOP === 'true' && console.log("MSG:", responseVinculo.data.msg);
        }
      } else {
        setselectData({
          value: "",
          label: "",
        });
      }
    }
  };

  const toggleUserOptions = () => {
    const newState = !showUserOptions;

    if (newState) {
      // PRIMEIRO posicionar, DEPOIS mostrar
      positionAndShowDropdown();
    } else {
      setShowUserOptions(false);
    }
  };

  // Função para posicionar e mostrar o dropdown das opções do usuário
  const positionAndShowDropdown = () => {
    if (!userOptionsButtonRef.current || !userOptionsDropdownRef.current) return;

    const buttonRect = userOptionsButtonRef.current.getBoundingClientRect();
    const dropdown = userOptionsDropdownRef.current;

    // Verificar se o botão está visível
    if (buttonRect.width === 0 && buttonRect.height === 0) return;

    // Calcular posição baseada em tamanho estimado do dropdown (180px width, ~120px height)
    const estimatedWidth = 180;
    const estimatedHeight = 140;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Posição inicial (abaixo do botão)
    let finalX = buttonRect.left;
    let finalY = buttonRect.bottom + 8;

    // Verificar se sairia da direita da tela
    if (finalX + estimatedWidth > windowWidth - 10) {
      finalX = buttonRect.right - estimatedWidth;
    }

    // Verificar se sairia da esquerda da tela
    if (finalX < 10) {
      finalX = 10;
    }

    // Verificar se sairia da parte inferior da tela
    if (finalY + estimatedHeight > windowHeight - 10) {
      finalY = buttonRect.top - estimatedHeight - 8;
    }

    // Verificar se sairia da parte superior da tela
    if (finalY < 10) {
      finalY = 10;
    }

    // Posicionar ANTES de mostrar
    dropdown.style.left = `${finalX}px`;
    dropdown.style.top = `${finalY}px`;

    // Agora mostrar o dropdown
    setShowUserOptions(true);
  };


  const toggleMobileStatusSidebar = () => {
    setShowMobileStatusSidebar(!showMobileStatusSidebar);
  };

  const closeMobileStatusSidebar = () => {
    setShowMobileStatusSidebar(false);
  };

  const showModalAddUserImg = () => {
    setModalImg(!showModalImg);
    setShowUserOptions(false);
  };

  // ====================================================================
  // HANDLER PARA FECHAR DROPDOWN QUANDO CLICAR FORA
  // ====================================================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar se clicou fora do botão E fora do dropdown (Portal)
      const clickedOutsideButton = userOptionsRef.current && !userOptionsRef.current.contains(event.target);
      const clickedOutsideDropdown = userOptionsDropdownRef.current && !userOptionsDropdownRef.current.contains(event.target);

      if (clickedOutsideButton && clickedOutsideDropdown) {
        setShowUserOptions(false);
      }
    };

    if (showUserOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserOptions]);

  // ====================================================================
  // FECHAR DROPDOWN EM SCROLL PARA EVITAR DESALINHAMENTO
  // ====================================================================
  useEffect(() => {
    if (!showUserOptions) return;

    const handleScroll = () => {
      setShowUserOptions(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showUserOptions]);

  // Fechar dropdown de status ao rolar
  useEffect(() => {
    if (!dropstatusrobo) return;

    const handleScroll = () => {
      setDropStatusRobo(false);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [dropstatusrobo]);

  // ====================================================================
  // FUNÇÃO DE NOTIFICAÇÕES
  // ====================================================================
  const sendNotification = (title, options) => {
    if (!supportsReliableNotifications()) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("📱 Notificação não enviada - dispositivo não suporta notificações confiáveis");
      return;
    }

    if (Notification.permission === "granted") {
      const notification = new Notification(title, {
        ...options,
        icon: brandIcon,
      });

      notification.onshow = () => {
        const audio = new Audio(audioNotify);
        audio
          .play()
          .catch((error) =>
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Erro ao reproduzir o som da notificação:", error)
          );
      };
    } else {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔔 Notificação não enviada - permissão não concedida");
    }
  };

  const handleNotify = useCallback(() => {
    const title = t('orders:notifications.newOrderTitle');
    const options = {
      body: t('orders:newOrderReceived'),
      sound: audioNotify,
    };
    sendNotification(title, options);
  }, []);

  // ====================================================================
  // FUNÇÕES DE FECHAMENTO DE AVISOS
  // ====================================================================
  const closeTrialAlert = () => {
    setIsTrialAlertFading(true);
    setTimeout(() => {
      setShowTrialAlert(false);
      setIsTrialAlertFading(false);
    }, 300);
  };

  const closeExpirationAlert = () => {
    setIsExpirationAlertFading(true);
    setTimeout(() => {
      setShowExpirationAlert(false);
      setIsExpirationAlertFading(false);
    }, 300);
  };



  // ====================================================================
  // EFFECTS PARA CLICK OUTSIDE
  // ====================================================================
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideOptions);
    document.addEventListener("mousedown", handleClickOutsideStatusRobo);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideOptions);
      document.removeEventListener("mousedown", handleClickOutsideStatusRobo);
    };
  }, []);

  // ✅ CORREÇÃO: Forçar re-layout após montagem do componente para corrigir problemas de CSS após redirecionamentos
  useEffect(() => {
    const forceLayoutUpdate = () => {
      // Disparar evento resize para forçar recalculo do layout
      window.dispatchEvent(new Event('resize'));
      
      // Forçar repaint do navbar
      const navbar = document.querySelector('.leftmenu-navbar');
      if (navbar) {
        navbar.style.display = 'none';
        // eslint-disable-next-line no-unused-expressions
        navbar.offsetHeight; // Trigger reflow
        navbar.style.display = 'flex';
      }
    };

    // Executar após um pequeno delay para garantir que o DOM esteja estável
    const timeoutId = setTimeout(forceLayoutUpdate, 100);
    
    return () => clearTimeout(timeoutId);
  }, []); // Executar apenas na montagem

  // ✅ INICIALIZAÇÃO COMPLETA: Marcar como inicializado após todos os efeitos
  useEffect(() => {
    // Aguardar todos os outros useEffects finalizarem e dados serem carregados
    const initializationTimeout = setTimeout(() => {
      setIsInitialized(true);
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log('[LEFTMENU] Componente totalmente inicializado');
    }, 200);

    return () => clearTimeout(initializationTimeout);
  }, []); // Executar apenas na montagem

  const handleClickOutsideOptions = (y) => {
    if (!y.target || !userOptionsRef.current) return;
    if (userOptionsRef.current.contains(y.target)) {
      return;
    } else {
      setUserOptions(true);
    }
  };

  const handleClickOutsideStatusRobo = (y) => {
    if (!y.target) return;
    const isClickOnButton = menuStatusRoboRef.current && menuStatusRoboRef.current.contains(y.target);
    const isClickOnDropdown = menuStatusRoboRef_.current && menuStatusRoboRef_.current.contains(y.target);
    
    if (isClickOnButton || isClickOnDropdown) {
      return;
    }
    setDropStatusRobo(false);
  };

  // ====================================================================
  // EFFECT PARA RESIZE
  // ====================================================================
  const handleResize = useCallback(() => {
    const currentWidth = window.innerWidth;
    if (currentWidth < 1300) {
      setSidebar(false);
    }
  }, [setSidebar]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  // ====================================================================
  // EFFECT PARA DADOS DA EMPRESA
  // ====================================================================
  useEffect(() => {
    const fetchData = async () => {
      try {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔄 Atualizando informações do vínculo da empresa...");
        const response = await getVinculoEmpresa(userID);
        setStatusLojaTemporario(response.data.vinculo.fechamento_temporario);
        setStatusBot(response.data.vinculo.status_bot);
        setCallAtendente(response.data.vinculo.call_atendente);
        setStatusPrinter(response.data.vinculo.status_printer);
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("response.data.vinculo:", response.data.vinculo);
        const responseCheckLicense = await getDaysToExpireLicense(response.data.vinculo._id);
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("responseCheckLicense:", responseCheckLicense);
        
        if (responseCheckLicense.status === 204) {
          setSemCobrancaOuInvoice(true);
        }

        // Backend V2 retorna: diasParaExpirar, status, statusPlano
        const _daysVal = responseCheckLicense.data.diasParaExpirar ?? "";
        const _planVal = responseCheckLicense.data.statusPlano || responseCheckLicense.data.status || "";
        setDaysToExpire(_daysVal);
        setPlanType(_planVal);
        setCarregandoCheckLicense(false);
      } catch (error) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.error("❌ Erro ao obter dados:", error);
        setCarregandoCheckLicense(false);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 3600000);
    return () => clearInterval(intervalId);
  }, [userID]);

  // ====================================================================
  // EFFECT PARA PROGRESSO DA CONFIGURAÇÃO INICIAL
  // ====================================================================
  useEffect(() => {
    if (!objIdEmpresa) return;

    const verificarProgressoConfiguracaoInicial = async () => {
      try {
        const response = await getEmpresaWithObjId(objIdEmpresa);
        const empresa = response.data.empresa;
        let progressoAtual = 0;

        if (empresa.configuracao_inicial && empresa.configuracao_inicial.etapas_completas) {
          progressoAtual = empresa.configuracao_inicial.etapas_completas.length;
        } else if (empresa.progresso_configuracao_inicial) {
          progressoAtual = empresa.progresso_configuracao_inicial;
        }

        process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Progresso configuração inicial:', {
          progressoAtual,
          totalEtapas: totalEtapasConfiguracaoInicial,
          configuracaoInicial: empresa.configuracao_inicial
        });

        setProgressoConfiguracaoInicial(progressoAtual);
        const configuracaoCompleta = empresa.configuracao_inicial && empresa.configuracao_inicial.finalizada;
        setMostrarComecePorAqui(!configuracaoCompleta && progressoAtual < totalEtapasConfiguracaoInicial);
      } catch (error) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao verificar progresso da configuração inicial:", error);
      }
    };

    verificarProgressoConfiguracaoInicial();
  }, [objIdEmpresa, totalEtapasConfiguracaoInicial]);

  // ====================================================================
  // EFFECTS PARA AVISOS DE LICENÇA
  // ====================================================================
  useEffect(() => {
    setShowTrialAlert(true);
    setShowExpirationAlert(true);
    setIsTrialAlertFading(false);
    setIsExpirationAlertFading(false);
  }, [planType, daysToExpire, diasTesteRestantes]);

  // Alertas permanecem visíveis até o usuário fechar manualmente

  // ====================================================================
  // EFFECT PARA WEBSOCKET
  // ====================================================================
  useEffect(() => {
    if (!objIdEmpresa) return;

    if (socketRef.current) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔄 Desconectando socket anterior...");
      socketRef.current.disconnect();
    }

    process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`🔌 Conectando ao WebSocket para a empresa ${objIdEmpresa.toString()}...`);

    const isDevelopment = window.location.hostname === "localhost";
    const apiUrl = isDevelopment
      ? process.env.REACT_APP_SERVER_URL_DEV
      : process.env.REACT_APP_SERVER_URL_PROD;

    const socket = io(apiUrl, {
      withCredentials: true,
      transports: ["websocket"],
      auth: { token: getCurrentAccessToken() },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 5000,
    });

    socketRef.current = socket;

    socket.emit("joinCompanyRoom", {
      companyId: objIdEmpresa.toString(),
      clientId: "reactClient",
    });

    socket.on("connect", () => {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`✅ Conectado ao WebSocket! Socket ID: ${socket.id}`);
      socket.emit("statusRequest", { companyId: objIdEmpresa.toString() });
    });

    socket.on("reconnect_attempt", (attempt) => {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`🔄 Tentativa de reconexão (${attempt}/10)...`);
    });

    socket.on("reconnect", (attempt) => {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`✅ Reconectado ao WebSocket após ${attempt} tentativas!`);
      socket.emit("statusRequest", { companyId: objIdEmpresa.toString() });
    });

    socket.on("connect_error", (error) => {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.error("❌ Erro na conexão WebSocket:", error);
    });

    socket.on("novoPedido", () => {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("📦 NOVO PEDIDO RECEBIDO!");
      handleNotify();
    });

    socket.on("statusUpdate", ({ companyId: updatedCompanyId, status }) => {
      if (objIdEmpresa.toString() === updatedCompanyId) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`🖨️ Status atualizado da impressora: ${status}`);
        setStatusPrinter(status === "Online" ? "Online" : "Offline");
      }
    });

    return () => {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🛑 Desconectando WebSocket ao desmontar ou mudar empresa...");
      socket.off("novoPedido");
      socket.off("statusUpdate");
      socket.off("reconnect_attempt");
      socket.off("reconnect");
      socket.off("connect_error");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [objIdEmpresa, handleNotify]);

  // ====================================================================
  // EFFECT PARA PERMISSÕES DE NOTIFICAÇÃO
  // ====================================================================
  useEffect(() => {
    if (!supportsReliableNotifications()) {
      if (isIOS()) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("📱 Dispositivo iOS detectado - notificações web não são solicitadas para melhor experiência do usuário");
      } else if (!("Notification" in window)) {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.warn("⚠️ Este navegador não suporta notificações de sistema.");
      }
    } else if (Notification.permission !== "denied") {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔔 Solicitando permissão para notificações...");
      Notification.requestPermission().then(permission => {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔔 Permissão para notificações:", permission);
      });
    }
  }, []);

  // ====================================================================
  // RENDERIZAÇÃO DO COMPONENTE
  // ====================================================================
  // ✅ Aguardar inicialização completa antes de renderizar
  if (!isInitialized) {
    return (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '80px', 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '14px', color: '#64748b' }}>{t('common:messages.loading')}</div>
      </div>
    );
  }

  return (
    <>
      {/* ====================================================================
          MODAIS
          ==================================================================== */}
      <ModalUserImg
        setModalImg={setModalImg}
        showModalImg={showModalImg}
        userID={userID}
      />

      <ModalEditUser
        setEditUser={setEditUser}
        showEditUser={showEditUser}
        //setRefresh={setRefresh}
        selectData={selectData}
        setselectData={setselectData}
        _idUserEdit={_idUserEdit}
        usernameEdit={usernameEdit}
        emailEdit={emailEdit}
        roleEdit={roleEdit}
        editPerfil={true}
      />

      <ModalLinkCardapio
        showLinkCardapio={showLinkCardapio}
        setShowLinkCardapio={setShowLinkCardapio}
        nomeEmpresaForUrl={nomeEmpresaForUrl}
      />

      <ModalLinkCardapioSalao
        showLinkCardapioSalao={showLinkCardapioSalao}
        setShowLinkCardapioSalao={setShowLinkCardapioSalao}
        nomeEmpresaForUrl={nomeEmpresaForUrl}
      />

      {/* Modal de confirmação para alterar status da loja */}
      {showStoreStatusModal && createPortal(
        <>
          <ModalOverlay onClick={() => !isChangingStoreStatus && setShowStoreStatusModal(false)} />
          <ModalContainer>
            <ModalHeader>
              <ModalIconWrapper $isClosing={!statusLojaTemporario}>
                <MdStorefront />
              </ModalIconWrapper>
              <ModalTitle>
                {statusLojaTemporario
                  ? t('common:storeStatus.openStoreTitle')
                  : t('common:storeStatus.closeStoreTitle')}
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <ModalMessage>
                {statusLojaTemporario
                  ? t('common:storeStatus.openStoreMessage')
                  : t('common:storeStatus.closeStoreMessage')}
              </ModalMessage>
              {!statusLojaTemporario && (
                <ModalWarning $isClosing={true}>
                  <IoWarningOutline />
                  <span>{t('common:storeStatus.closeStoreWarning')}</span>
                </ModalWarning>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalButton
                onClick={() => setShowStoreStatusModal(false)}
                disabled={isChangingStoreStatus}
              >
                {statusLojaTemporario
                  ? t('common:storeStatus.keepClosed')
                  : t('common:storeStatus.keepOpen')}
              </ModalButton>
              <ModalButton
                $primary
                $isClosing={!statusLojaTemporario}
                onClick={handleConfirmStoreStatus}
                disabled={isChangingStoreStatus}
              >
                {isChangingStoreStatus
                  ? t('common:buttons.loading')
                  : statusLojaTemporario
                    ? t('common:storeStatus.confirmOpen')
                    : t('common:storeStatus.confirmClose')}
              </ModalButton>
            </ModalFooter>
          </ModalContainer>
        </>,
        document.body
      )}

      <IconContext.Provider value={{ className: "leftmenu-icon" }}>
        {/* ====================================================================
            BOTÃO MOBILE DE STATUS
            ==================================================================== */}
        <button 
          className="leftmenu-mobile-status-toggle" 
          onClick={toggleMobileStatusSidebar}
          title={t('common:systemStatus')}
        >
          <MdSettings />
        </button>

        {/* ====================================================================
            SIDEBAR MOBILE DE STATUS
            ==================================================================== */}
        {showMobileStatusSidebar && (
          <div 
            className="leftmenu-mobile-status-overlay leftmenu-mobile-status-overlay--visible"
            onClick={closeMobileStatusSidebar}
          />
        )}
        
        <div className={`leftmenu-mobile-status-sidebar ${showMobileStatusSidebar ? 'leftmenu-mobile-status-sidebar--open' : ''}`}>
          <div className="leftmenu-mobile-status-sidebar-header">
            <h3>{t('info.title')}</h3>
            <button
              className="leftmenu-mobile-status-sidebar-close"
              onClick={closeMobileStatusSidebar}
            >
              ×
            </button>
          </div>

          <div className="leftmenu-mobile-status-sidebar-content">
            {/* Status Robo */}
            <div className="leftmenu-mobile-status-item" onClick={handleSwitchChange}>
              <FaRobot className="leftmenu-mobile-status-item-icon" style={{ color: statusBot ? '#10b981' : '#ef4444' }} />
              <div className="leftmenu-mobile-status-item-text">
                <div className="leftmenu-mobile-status-item-label">Status</div>
                <div className="leftmenu-mobile-status-item-value">{t('common:leftMenu.pedejaBot')} {statusBot ? 'ON' : 'OFF'}</div>
              </div>
            </div>

            {/* Impressora */}
            <div className="leftmenu-mobile-status-item">
              <ImPrinter
                className="leftmenu-mobile-status-item-icon"
                style={{ color: statusPrinter === "Offline" ? '#ef4444' : '#10b981' }}
              />
              <div className="leftmenu-mobile-status-item-text">
                <div className="leftmenu-mobile-status-item-label">{t('menu.printer')}</div>
                <div className="leftmenu-mobile-status-item-value">
                  {statusPrinter === "Offline" ? 'Offline' : t('status.active')}
                </div>
              </div>
            </div>

            {/* Cardápio Delivery */}
            <div className="leftmenu-mobile-status-item" onClick={() => setShowLinkCardapio(true)}>
              <img src={entregadorIco} className="leftmenu-mobile-status-item-icon" alt="Delivery" />
              <div className="leftmenu-mobile-status-item-text">
                <div className="leftmenu-mobile-status-item-label">Link</div>
                <div className="leftmenu-mobile-status-item-value">{t('menu.menuDelivery')}</div>
              </div>
            </div>

            {/* Cardápio Salão */}
            <div className="leftmenu-mobile-status-item" onClick={() => setShowLinkCardapioSalao(true)}>
              <img src={roundTable} className="leftmenu-mobile-status-item-icon" alt={t('menu.menuDineIn')} />
              <div className="leftmenu-mobile-status-item-text">
                <div className="leftmenu-mobile-status-item-label">Link</div>
                <div className="leftmenu-mobile-status-item-value">{t('menu.menuDineIn')}</div>
              </div>
            </div>

            {/* Status da Loja */}
            <div className="leftmenu-mobile-status-item" onClick={handleOpenCloseLoja}>
              <MdStorefront className="leftmenu-mobile-status-item-icon" style={{ color: 'black' }} />
              <div className="leftmenu-mobile-status-item-text">
                <div className="leftmenu-mobile-status-item-label">{t('header.storeOpen').split(' ')[0]} {statusLojaTemporario ? t('status.closed') : t('status.open')}</div>
                <div className="leftmenu-mobile-status-item-value">{nomeEmpresa}</div>
                <button
                  className="leftmenu-action-button"
                  style={{ marginTop: '8px' }}
                >
                  {statusLojaTemporario ? t('header.storeOpen') : t('header.storeClosed')}
                </button>
              </div>
            </div>

            {/* Logout */}
            <div className="leftmenu-mobile-status-item" onClick={handleLogout} style={{ marginTop: '20px' }}>
              <MdExitToApp className="leftmenu-mobile-status-item-icon" style={{ color: '#ef4444' }} />
              <div className="leftmenu-mobile-status-item-text">
                <div className="leftmenu-mobile-status-item-label">{t('header.logout')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ====================================================================
            NAVBAR PRINCIPAL
            ==================================================================== */}
        <nav className={`leftmenu-navbar ${sidebar ? '' : 'leftmenu-navbar--collapsed'}`}>
          <div className="leftmenu-navbar-left">
            <div 
              onClick={showDrawer} 
              className="leftmenu-mobile-toggle leftmenu-desktop-hidden"
            >
              <GiHamburgerMenu />
            </div>
            {/*<div className="leftmenu-logo-container leftmenu-hidden-mobile">
              <img 
                src={logoImg} 
                alt="pede-ja logo" 
                className="leftmenu-logo"
              />
            </div>*/}
          </div>

          <div className="leftmenu-navbar-right">
            {/* Seletor de Idioma */}
            <CustomTooltip content={`${t('language.title')}: ${i18n.language === 'pt-BR' ? t('language.portuguese') : t('language.english')}`}>
              <div className="leftmenu-navbar-section leftmenu-navbar-section--language">
                <div className="leftmenu-navbar-section-content">
                  <MdLanguage className="leftmenu-navbar-section-icon" />
                  <select
                    value={i18n.language}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="leftmenu-language-select"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="pt-BR">🇧🇷 PT</option>
                    <option value="en-US">🇺🇸 EN</option>
                  </select>
                </div>
              </div>
            </CustomTooltip>

            {/* Seção Status Robô */}
            <CustomTooltip content={`${t('common:leftMenu.pedejaBot')}: ${statusBot ? t('status.active') : t('status.inactive')}`}>
              <div
                className="leftmenu-navbar-section leftmenu-navbar-section--dropdown"
                ref={menuStatusRoboRef}
                onClick={handleToggleStatusRobo}
              >
                <div className="leftmenu-navbar-section-content">
                  <FaRobot className="leftmenu-navbar-section-icon" style={{ color: statusBot ? '#10b981' : '#64748b' }} />
                  <div className="leftmenu-navbar-section-text leftmenu-hidden-mobile">
                    <span className="leftmenu-navbar-section-label">Status</span>
                    <span className="leftmenu-navbar-section-value">{t('common:leftMenu.pedejaBot')} <RiArrowDropDownLine style={{ verticalAlign: 'middle' }} /></span>
                  </div>
                </div>
              </div>
            </CustomTooltip>

            {/* Seção Impressora */}
            <CustomTooltip content={`${t('menu.printer')}: ${statusPrinter === "Offline" ? 'Offline' : t('status.active')}`}>
              <div className="leftmenu-navbar-section" onClick={() => navigate('/impressora')}>
                <div className="leftmenu-navbar-section-content">
                  <ImPrinter
                    className="leftmenu-navbar-section-icon"
                    style={{ color: statusPrinter === "Offline" ? '#ef4444' : '#10b981' }}
                  />
                  <div className="leftmenu-navbar-section-text leftmenu-hidden-mobile">
                    <span className="leftmenu-navbar-section-label">{t('menu.printer')}</span>
                    <div className={`leftmenu-status-badge ${statusPrinter === "Offline" ? 'leftmenu-status-badge--closed' : 'leftmenu-status-badge--open'}`}>
                      {statusPrinter === "Offline" ? 'Offline' : t('status.active')}
                    </div>
                  </div>
                </div>
              </div>
            </CustomTooltip>

            {/* Seção Cardápio Delivery */}
            <CustomTooltip content={t('menu.menuDelivery')}>
              <div
                className="leftmenu-navbar-section"
                onClick={() => setShowLinkCardapio(true)}
              >
                <div className="leftmenu-navbar-section-content">
                  <HiIcons.HiOutlineLink className="leftmenu-navbar-section-icon" />
                  <div className="leftmenu-navbar-section-text leftmenu-hidden-mobile">
                    <span className="leftmenu-navbar-section-label">Link</span>
                    <span className="leftmenu-navbar-section-value">{t('menu.menuDelivery')}</span>
                  </div>
                </div>
              </div>
            </CustomTooltip>

            {/* Seção Cardápio Salão */}
            <CustomTooltip content={t('menu.menuDineIn')}>
              <div
                className="leftmenu-navbar-section"
                onClick={() => setShowLinkCardapioSalao(true)}
              >
                <div className="leftmenu-navbar-section-content">
                  <HiIcons.HiOutlineExternalLink className="leftmenu-navbar-section-icon" />
                  <div className="leftmenu-navbar-section-text leftmenu-hidden-mobile">
                    <span className="leftmenu-navbar-section-label">Link</span>
                    <span className="leftmenu-navbar-section-value">{t('menu.menuDineIn')}</span>
                  </div>
                </div>
              </div>
            </CustomTooltip>

            {/* Seção Status da Loja */}
            <CustomTooltip content={`${nomeEmpresa}: ${statusLojaTemporario ? t('status.closed') : t('status.open')}`}>
              <div className="leftmenu-navbar-section leftmenu-hidden-sm-mobile">
                <div className="leftmenu-store-wrapper">
                  <div className="leftmenu-store-header">
                    <MdStorefront className="leftmenu-navbar-section-icon" style={{ color: statusLojaTemporario ? '#ef4444' : '#10b981' }} />
                    <span className="leftmenu-navbar-section-label">{t('common:store')}</span>
                    <div className={`leftmenu-status-badge ${statusLojaTemporario ? 'leftmenu-status-badge--closed' : 'leftmenu-status-badge--open'}`}>
                      {statusLojaTemporario ? t('status.closed') : t('status.open')}
                    </div>
                  </div>
                  <div className="leftmenu-store-actions">
                    <button
                      className="leftmenu-store-btn leftmenu-store-btn--close"
                      onClick={handleOpenCloseLoja}
                      disabled={statusLojaTemporario}
                    >
                      {t('header.storeClosed')}
                    </button>
                    <button
                      className="leftmenu-store-btn leftmenu-store-btn--open"
                      onClick={handleOpenCloseLoja}
                      disabled={!statusLojaTemporario}
                    >
                      {t('header.storeOpen')}
                    </button>
                  </div>
                </div>
              </div>
            </CustomTooltip>

            {/* Seção Sair */}
            <div className="leftmenu-exit-section leftmenu-hidden-mobile" onClick={handleLogout}>
              <MdExitToApp />
              <span>{t('header.logout')}</span>
            </div>
          </div>
        </nav>

        {/* ====================================================================
            DRAWER MOBILE
            ==================================================================== */}
        <CustomDrawer
          title={t('mobile.menu')}
          onClose={onClose}
          open={open}
          placement="left"
          bodyStyle={{ padding: 0 }}
          className="leftmenu-mobile-drawer"
        >
          <div className="leftmenu-mobile-drawer-content">
            <div className="leftmenu-navigation-area leftmenu-scrollbar">
              {SidebarData.map((item, index) => {
                if (!canViewMenuItem(item)) return null;
                return (
                  <PermissionGate key={index} permissions={[SidebarData[index].permission]}>
                    <SubMenu item={item} onMobileMenuClose={onClose} />
                  </PermissionGate>
                );
              })}
            </div>
            <div className="leftmenu-mobile-drawer-footer">
              <div className="leftmenu-navbar-section">
                <div className="leftmenu-navbar-section-content">
                  <MdStorefront style={{ color: "black", fontSize: 20 }} />
                  <div className="leftmenu-navbar-section-text">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="leftmenu-navbar-section-label">{t('header.storeOpen').split(' ')[0]}</span>
                      <div className={`leftmenu-status-badge ${statusLojaTemporario ? 'leftmenu-status-badge--closed' : 'leftmenu-status-badge--open'}`}>
                        <span>{statusLojaTemporario ? t('status.closed') : t('status.open')}</span>
                      </div>
                    </div>
                    <span className="leftmenu-navbar-section-value">{nomeEmpresa}</span>
                    <button
                      className="leftmenu-action-button"
                      onClick={handleOpenCloseLoja}
                    >
                      {statusLojaTemporario ? t('header.storeOpen') : t('header.storeClosed')}
                    </button>
                  </div>
                </div>
              </div>
              <div className="leftmenu-exit-section" onClick={handleLogout}>
                <MdExitToApp style={{ fontSize: 24 }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('header.logout')}</span>
              </div>
            </div>
          </div>
        </CustomDrawer>

        {/* ====================================================================
            BANNERS DE INFORMAÇÃO
            ==================================================================== */}
        {(!planType || planType === "" || daysToExpire === 0) && diasTesteRestantes > 0 && showTrialAlert &&
         !semCobrancaOuInvoice && !carregandoCheckLicense && (
          <div
            className={`leftmenu-info-banner leftmenu-animate-in ${isTrialAlertFading ? 'leftmenu-invisible' : ''}`}
            style={{
              left: sidebar ? '250px' : '100px',
              opacity: isTrialAlertFading ? 0 : 1,
              transition: 'left 150ms, opacity 0.3s ease, transform 0.3s ease',
            }}
          >
            <span>{t('common:billing.trialDaysRemaining', { days: diasTesteRestantes, count: diasTesteRestantes })}</span>
            <button
              onClick={closeTrialAlert}
              className="leftmenu-close-btn"
              title={t('common:billing.closeAlert')}
            >
              ✕
            </button>
          </div>
        )}

        {planType && daysToExpire !== "" && daysToExpire <= 3 && diferencaEmDias > 7 && showExpirationAlert ? (
          <div
            className={`leftmenu-info-banner leftmenu-animate-in ${isExpirationAlertFading ? 'leftmenu-invisible' : ''}`}
            style={{
              left: sidebar ? '250px' : '100px',
              opacity: isExpirationAlertFading ? 0 : 1,
              transition: 'left 150ms, opacity 0.3s ease, transform 0.3s ease',
            }}
          >
            <div style={{ flex: 1, textAlign: 'center' }}>
              {daysToExpire < 0 ? (
                <span>
                  {t('common:billing.planExpired', { defaultValue: 'Seu plano expirou. Renove para continuar usando o sistema.' })}
                </span>
              ) : daysToExpire === 0 ? (
                <span>
                  {t('common:billing.planExpiringToday', { defaultValue: 'Seu plano expira hoje. Renove para não perder o acesso.' })}
                </span>
              ) : (
                <span>
                  {t('common:billing.daysUntilExpiration', { days: daysToExpire, count: daysToExpire })}
                </span>
              )}
            </div>
            <button
              onClick={closeExpirationAlert}
              className="leftmenu-close-btn"
              title={t('common:billing.closeAlert')}
            >
              ✕
            </button>
          </div>
        ) : null}

        {/* ====================================================================
            MENU DE AJUDA E ATENDIMENTOS - BOTÃO REDESENHADO
            ==================================================================== */}
        <div className="leftmenu-help-menu-container">
          <div
            ref={atendimentoButtonRef}
            className={`leftmenu-atendimento-btn ${atendimentosPendentes.length > 0 ? 'leftmenu-atendimento-btn--active' : ''} ${modalOpen ? 'leftmenu-atendimento-btn--open' : ''}`}
            onClick={() => {
              process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔍 Clicou no ícone de atendimentos:", {
                atendimentosPendentes,
                length: atendimentosPendentes?.length,
                modalOpen
              });
              setModalOpen(!modalOpen);
            }}
          >
            <div className="leftmenu-atendimento-btn-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Headset icon */}
                <path
                  d="M4 12V9C4 5.13 7.13 2 11 2H13C16.87 2 20 5.13 20 9V12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M2 15.5C2 14.12 3.12 13 4.5 13H5C5.55 13 6 13.45 6 14V19C6 19.55 5.55 20 5 20H4.5C3.12 20 2 18.88 2 17.5V15.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M22 15.5C22 14.12 20.88 13 19.5 13H19C18.45 13 18 13.45 18 14V19C18 19.55 18.45 20 19 20H19.5C20.88 20 22 18.88 22 17.5V15.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M6 19V20C6 21.1 6.9 22 8 22H12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>

              {/* Notification ring animation */}
              {atendimentosPendentes.length > 0 && (
                <span className="leftmenu-atendimento-btn-ring"></span>
              )}
            </div>

            {/* Badge contador */}
            {atendimentosPendentes.length > 0 && (
              <div className="leftmenu-atendimento-btn-badge">
                {atendimentosPendentes.length > 9 ? '9+' : atendimentosPendentes.length}
              </div>
            )}

            {/* Loading state */}
            {isLoadingAtendimentos && atendimentosPendentes.length === 0 && (
              <div className="leftmenu-atendimento-btn-loading"></div>
            )}
          </div>
        </div>

        {/* ====================================================================
            DROPDOWN DE ATENDIMENTOS PENDENTES
            ==================================================================== */}
        <AtendimentoModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          atendimentosPendentes={atendimentosPendentes}
          cancelarTodosAtendimentos={cancelarTodosAtendimentos}
          cancelarAtendimento={cancelarAtendimento}
          iniciarAtendimento={iniciarAtendimento}
          isLoading={isLoadingAtendimentos}
          buttonRef={atendimentoButtonRef}
        />


        {/* ====================================================================
            SIDEBAR PRINCIPAL
            ==================================================================== */}
        <nav
          ref={leftMenuRef}
          className={`leftmenu-sidebar ${sidebar ? '' : 'leftmenu-sidebar--collapsed'} leftmenu-animate-slide-in`}
        >
          <div className="leftmenu-sidebar-wrapper">
            {/* Header Compacto - Estilo Google/Notion */}
            <div className="leftmenu-header-compact">
              {/* Área clicável do usuário */}
              <div
                ref={userOptionsRef}
                className={`leftmenu-user-trigger ${showUserOptions ? 'leftmenu-user-trigger--active' : ''}`}
                onClick={toggleUserOptions}
              >
                <SafeImage
                  ref={userOptionsButtonRef}
                  src={userImg !== null && userImg.length > 0 ? userImg : defaultUserImg}
                  fallback={defaultUserImg}
                  className="leftmenu-compact-avatar"
                  alt="Avatar"
                />
                <div className={`leftmenu-compact-info ${sidebar ? '' : 'leftmenu-compact-info--hidden'}`}>
                  <span className="leftmenu-compact-name">{userName}</span>
                  <span className="leftmenu-compact-email">{userEmail}</span>
                </div>
              </div>

              {/* Botão de Colapsar/Expandir com Tooltip */}
              <div className="leftmenu-collapse-btn-wrapper">
                <CustomTooltip content={sidebar ? t('tooltips.collapseMenu') : t('tooltips.expandMenu')}>
                  <div
                    className="leftmenu-collapse-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSidebar();
                    }}
                  >
                    {sidebar ? <RiMenuFoldFill /> : <RiMenuUnfoldFill />}
                  </div>
                </CustomTooltip>
              </div>
            </div>

            {/* PORTAL: Dropdown de opções do usuário - Estilo Google */}
            {createPortal(
              <div
                ref={userOptionsDropdownRef}
                className={`leftmenu-user-options-dropdown ${showUserOptions ? 'leftmenu-user-options-dropdown--visible' : ''}`}
              >
                {/* Header do dropdown com info do usuário */}
                <div className="leftmenu-dropdown-header">
                  <SafeImage
                    src={userImg !== null && userImg.length > 0 ? userImg : defaultUserImg}
                    fallback={defaultUserImg}
                    className="leftmenu-dropdown-avatar"
                    alt="Avatar"
                  />
                  <div className="leftmenu-dropdown-user-info">
                    <span className="leftmenu-dropdown-name">{userName}</span>
                    <span className="leftmenu-dropdown-email">{userEmail}</span>
                  </div>
                </div>

                {/* Corpo com opções */}
                <div className="leftmenu-dropdown-body">
                  <div className="leftmenu-user-options-item" onClick={() => {
                    handleEdit(userID);
                    setShowUserOptions(false);
                  }}>
                    <HiIcons.HiOutlineUser />
                    <span>{t('header.profile')}</span>
                  </div>
                  <div className="leftmenu-user-options-item" onClick={showModalAddUserImg}>
                    <HiIcons.HiOutlinePhotograph />
                    <span>{t('sidebar.myData')}</span>
                  </div>
                  <div className="leftmenu-user-options-item" onClick={() => {
                    navigate('/informacoes-loja');
                    setShowUserOptions(false);
                  }}>
                    <MdStorefront />
                    <span>{t('header.myStore')}</span>
                  </div>
                  <div className="leftmenu-dropdown-divider" />
                  <div className="leftmenu-user-options-item leftmenu-user-options-item--logout" onClick={() => {
                    handleLogout();
                    setShowUserOptions(false);
                  }}>
                    <HiIcons.HiOutlineLogout />
                    <span>{t('header.logout')}</span>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* PORTAL: Dropdown de status do robô renderizado no body para evitar problemas de z-index */}
            {createPortal(
              <div
                ref={menuStatusRoboRef_}
                className={`leftmenu-status-dropdown-portal ${dropstatusrobo ? 'leftmenu-status-dropdown-portal--visible' : ''}`}
                style={{
                  top: `${statusDropdownPosition.top}px`,
                  left: `${statusDropdownPosition.left}px`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="leftmenu-status-dropdown-content">
                  <div className="leftmenu-status-dropdown-item">
                    <div className="leftmenu-status-dropdown-info">
                      <div className="leftmenu-status-dropdown-indicator"></div>
                      <div className="leftmenu-status-dropdown-label" onClick={handleSwitchChange}>
                        <svg width={18} height={18} fill="#797878" viewBox="0 0 13 13">
                          <path d="M9.477 7.79066C9.31409 7.70819 8.52069 7.32103 8.37241 7.26578C8.22412 7.21297 8.11647 7.18534 8.00841 7.34825C7.90278 7.50709 7.59159 7.87069 7.49653 7.97672C7.40147 8.08275 7.30803 8.09088 7.14756 8.01897C6.98466 7.9365 6.46425 7.76709 5.84634 7.21297C5.36372 6.78356 5.04237 6.25462 4.94691 6.09172C4.85184 5.93084 4.93634 5.83984 5.01678 5.75941C5.09072 5.68547 5.17969 5.57334 5.26216 5.47584C5.34056 5.37834 5.36575 5.31294 5.42303 5.20731C5.47584 5.09316 5.44862 5.00419 5.40841 4.92375C5.36819 4.84331 5.04441 4.04584 4.90912 3.72816C4.77994 3.41291 4.64466 3.45312 4.54512 3.45312C4.45209 3.44459 4.34403 3.44459 4.23638 3.44459C4.12872 3.44459 3.95281 3.48481 3.80453 3.63919C3.65625 3.80209 3.23741 4.19128 3.23741 4.97859C3.23741 5.76794 3.81712 6.53169 3.89756 6.64584C3.98003 6.75147 5.03791 8.37647 6.66087 9.07481C7.04803 9.23772 7.34866 9.33522 7.58347 9.41566C7.97062 9.53834 8.32406 9.52128 8.60316 9.48106C8.91191 9.43028 9.55947 9.08944 9.69516 8.70878C9.83288 8.32569 9.83288 8.00841 9.79266 7.9365C9.75244 7.86256 9.64681 7.82234 9.48391 7.75044L9.477 7.79066ZM6.53372 11.7812H6.52519C5.56441 11.7812 4.61459 11.5208 3.78503 11.0342L3.59044 10.918L1.55919 11.4469L2.10519 9.4705L1.97397 9.26738C1.4375 8.41439 1.1529 7.42722 1.15294 6.41956C1.15294 3.47019 3.56728 1.06437 6.53778 1.06437C7.97672 1.06437 9.32669 1.625 10.3423 2.64062C10.8433 3.13568 11.2407 3.72555 11.5114 4.37582C11.782 5.0261 11.9204 5.72376 11.9186 6.42809C11.9145 9.37544 9.50219 11.7812 6.53575 11.7812H6.53372ZM11.1146 1.86834C9.87878 0.674781 8.25378 0 6.52519 0C2.95994 0 0.056875 2.89047 0.0548438 6.44272C0.0548438 7.57697 0.351 8.68359 0.918125 9.66306L0 13L3.432 12.105C4.38275 12.6176 5.44547 12.8872 6.52559 12.8899H6.52763C10.0949 12.8899 12.998 9.99944 13 6.44475C13 4.72469 12.3293 3.10578 11.1065 1.88906L11.1146 1.86834Z" fill="#797878" />
                        </svg>
                        <div className="leftmenu-status-dropdown-text">
                          <span>Whatsapp</span>
                        </div>
                      </div>
                    </div>
                    <div className="leftmenu-switch-container">
                      <div
                        className={`leftmenu-switch ${statusBot ? 'leftmenu-switch--active' : ''}`}
                        onClick={handleSwitchChange}
                      ></div>
                    </div>
                  </div>

                  <div className="leftmenu-status-dropdown-item">
                    <div className="leftmenu-status-dropdown-info">
                      <div className="leftmenu-status-dropdown-indicator"></div>
                      <div className="leftmenu-status-dropdown-label" onClick={handleCallAtendenteChange}>
                        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#797878" strokeWidth={2}>
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx={9} cy={7} r={4} />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        <div className="leftmenu-status-dropdown-text">
                          <span>{t('common:leftMenu.call')}</span>
                          <span>{t('common:leftMenu.staff')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="leftmenu-switch-container">
                      <div
                        className={`leftmenu-switch ${callAtendente ? 'leftmenu-switch--active' : ''}`}
                        onClick={handleCallAtendenteChange}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )}

            {/* Área de Navegação */}
            <div className="leftmenu-navigation-area leftmenu-scrollbar">
              {/* Componente "Comece por aqui" */}
              {mostrarComecePorAqui && (
                <div
                  className="leftmenu-getting-started leftmenu-animate-in"
                  onClick={() => navigate('/configuracao-inicial')}
                >
                  <div className="leftmenu-getting-started-header">
                    <div className="leftmenu-getting-started-icon">
                      <FaArrowRight />
                    </div>
                    <div className="leftmenu-getting-started-text">
                      <div className="leftmenu-getting-started-title">{t('gettingStarted.title')}</div>
                      <div className="leftmenu-getting-started-subtitle">
                        {totalEtapasConfiguracaoInicial - progressoConfiguracaoInicial} {t('gettingStarted.pending').toLowerCase()}
                      </div>
                    </div>
                  </div>

                  <div className="leftmenu-getting-started-progress">
                    <div className="leftmenu-progress-bar-container">
                      <div
                        className="leftmenu-progress-bar"
                        style={{ width: `${(progressoConfiguracaoInicial / totalEtapasConfiguracaoInicial) * 100}%` }}
                      />
                    </div>
                    <div className="leftmenu-progress-stats">
                      <span>{progressoConfiguracaoInicial}/{totalEtapasConfiguracaoInicial} {t('gettingStarted.completed').toLowerCase()}</span>
                      <span>{Math.round((progressoConfiguracaoInicial / totalEtapasConfiguracaoInicial) * 100)}%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Itens de Menu - filtrados por RBAC + legacy PermissionGate */}
              {SidebarData.map((item, index) => {
                if (!canViewMenuItem(item)) return null;
                return (
                  <PermissionGate key={index} permissions={[SidebarData[index].permission]}>
                    <SubMenu item={item} />
                  </PermissionGate>
                );
              })}
            </div>
          </div>
        </nav>
      </IconContext.Provider>
    </>
  );
};

export default LeftMenu;

